/**
 * Cache Manager Service
 * Handles automatic cache clearing when app version changes
 */

import { APP_VERSION } from '../version';

const VERSION_KEY = 'app_version';
const RELOAD_FLAG_KEY = "app_reload_pending";

/**
 * Check if cache needs to be cleared based on app version
 * Call this on app startup
 * Returns true if cache was cleared and page will reload
 */
export const checkAndClearCache = async () => {
  const storedVersion = localStorage.getItem(VERSION_KEY);
  const reloadPending = sessionStorage.getItem(RELOAD_FLAG_KEY);

  // If we just reloaded, clear the flag and continue
  if (reloadPending === "true") {
    console.log("✅ Page reloaded after update - clearing reload flag");
    sessionStorage.removeItem(RELOAD_FLAG_KEY);
    localStorage.setItem(VERSION_KEY, APP_VERSION);
    return false;
  }

  // Check if version changed
  if (storedVersion !== APP_VERSION) {
    console.log(
      "🧹 Version mismatch detected - clearing cache and reloading..."
    );
    console.log(
      `📱 App updating from v${storedVersion || "unknown"} to v${APP_VERSION}`
    );

    // Clear all caches while preserving auth
    await clearAllCacheAndReload();
    return true; // Will reload
  } else {
    console.log("✅ Cache is up to date");
    return false;
  }
};

/**
 * Clear all caches and force reload the page
 * This ensures the latest bundle is loaded
 * Process is silent - no user notification
 */
// This runs AFTER new code is already on screen -- APP_VERSION comes from the
// running bundle, so a mismatch means the service worker has already swapped in
// a new build. Its job is only to drop app state left over from the old one.
//
// It deliberately no longer touches the service worker or the Cache API. It used
// to delete every cache and unregister every worker, which was actively harmful:
// the caches it wiped included workbox-precache, i.e. the copy of the app the
// worker had just installed, and unregistering removed the very mechanism that
// delivers updates. That left the page uncontrolled and dependent on the HTTP
// cache -- which, until the firebase.json header fix, served '/' with
// max-age=3600. The old comment here claimed "modern browsers treat programmatic
// reload() as hard reload"; they do not. location.reload() honors max-age, so the
// recovery reload could be handed back the very build it was trying to escape,
// and the reloadPending branch above would then stamp that stale version as
// current -- wedging the device with no path out.
//
// Update delivery belongs to the service worker alone: SKIP_WAITING ->
// controllerchange -> reload, in index.js and serviceWorkerRegistration.js.
export const clearAllCacheAndReload = async () => {
  console.log("🔄 Clearing stale app state after version change...");

  clearLocalStorageExceptAuth();

  // Survives the reload via sessionStorage; guards against a reload loop if the
  // new build somehow still reports a different version.
  sessionStorage.setItem(RELOAD_FLAG_KEY, "true");

  console.log("🚀 Reloading into the new build...");
  window.location.reload();
};

/**
 * Clear localStorage while preserving Firebase auth state
 */
const clearLocalStorageExceptAuth = () => {
  console.log("🗑️ Clearing localStorage (preserving auth)...");

  // Get all keys before clearing (for logging)
  const keys = Object.keys(localStorage);
  console.log(`  Found ${keys.length} items in localStorage`);

  // Preserve Firebase auth and essential keys
  const keysToKeep = [
    "firebase:authUser",
    "firebase:host",
    "firebase:apiKey",
    "firebase:heartbeat",
    VERSION_KEY,
  ];

  keys.forEach((key) => {
    const shouldKeep = keysToKeep.some((keepKey) => key.includes(keepKey));
    if (!shouldKeep) {
      console.log(`    Removing: ${key}`);
      localStorage.removeItem(key);
    } else {
      console.log(`    Keeping: ${key}`);
    }
  });

  console.log("✅ localStorage cleared (auth preserved)");
};

/**
 * Clear all cached data (legacy method - use clearAllCacheAndReload for full clear)
 */
export const clearAllCache = () => {
  console.log("🗑️ Clearing all cached data...");
  clearLocalStorageExceptAuth();
  console.log("✅ Cache cleared successfully");
};

/**
 * Force clear all cache including auth (use with caution)
 */
export const clearAllCacheIncludingAuth = () => {
  console.log('🗑️ Clearing ALL cache including auth...');
  localStorage.clear();
  sessionStorage.clear();
  console.log('✅ All cache cleared');
};

/**
 * Get current app version
 */
export const getCurrentVersion = () => {
  return APP_VERSION;
};

/**
 * Get stored version
 */
export const getStoredVersion = () => {
  return localStorage.getItem(VERSION_KEY);
};

/**
 * Manually update cache version (use after manual cache clear)
 */
export const updateCacheVersion = () => {
  localStorage.setItem(VERSION_KEY, APP_VERSION);
  console.log('✅ Cache version updated to:', APP_VERSION);
};

